// SGF export (export only — no parser).
// SGF coordinates: two letters a..s, x then y, with y=0 = TOP row (same
// orientation as Pt, no 'i' skipping unlike GTP).

import type { Vertex } from '../types.ts';
import type { GameRecord } from './game.ts';
import { isPass, isResign, vertexToPt } from './coords.ts';

const SGF_LETTERS = 'abcdefghijklmnopqrst';

function sgfPoint(vertex: Vertex, size: number): string {
  const pt = vertexToPt(vertex, size);
  const x = pt % size;
  const y = (pt - x) / size;
  return `${SGF_LETTERS.charAt(x)}${SGF_LETTERS.charAt(y)}`;
}

function escapeSgfText(text: string): string {
  return text.replace(/\\/g, '\\\\').replace(/]/g, '\\]');
}

export interface SgfOptions {
  result?: string | null;                     // e.g. 'B+5.5', 'W+R'
  playerBlack?: string;
  playerWhite?: string;
  date?: string;                              // 'YYYY-MM-DD'
}

/** Serialize a game record to SGF. 'resign' entries are skipped (use opts.result). */
export function toSGF(record: GameRecord, opts: SgfOptions = {}): string {
  const props = [
    'GM[1]',
    'FF[4]',
    'CA[UTF-8]',
    'AP[learn-baduk]',
    `SZ[${record.boardSize}]`,
    `KM[${record.komi}]`,
    'RU[Japanese]',
  ];
  if (opts.playerBlack) props.push(`PB[${escapeSgfText(opts.playerBlack)}]`);
  if (opts.playerWhite) props.push(`PW[${escapeSgfText(opts.playerWhite)}]`);
  if (opts.date) props.push(`DT[${escapeSgfText(opts.date)}]`);
  if (opts.result) props.push(`RE[${escapeSgfText(opts.result)}]`);

  let out = `(;${props.join('')}`;
  for (const move of record.moves) {
    if (isResign(move.vertex)) continue;
    const coord = isPass(move.vertex) ? '' : sgfPoint(move.vertex, record.boardSize);
    out += `;${move.color}[${coord}]`;
  }
  return `${out})`;
}
